import { type ReactNode } from "react";
import { DownloadIcon } from "vega-icons";

import { UnknownErrorModal } from "@vega-core/components/Modals/UnknownErrorModal";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { m } from "@vega-core/paraglide/messages";

import { ActionMenu, type MenuItem } from "../ActionMenu/ActionMenu";
import { ThemedButton, type ThemedButtonProps } from "../Buttons/ThemedButton";
import { Card } from "../shadcn/card";
import { type WithTooltipProps } from "../Tooltip/withTooltip";

type BulkActionsProps = {
  notice?: ReactNode;
  selectedCount: number;
  isLoading: boolean;
  isError?: boolean;
  onDownload?: () => void;
  onClear: () => void;
  primaryAction?: Omit<
    ThemedButtonProps & WithTooltipProps,
    "className" | "dataTestId" | "fullWidth"
  >;
  secondaryAction?: Omit<
    ThemedButtonProps & WithTooltipProps,
    "className" | "dataTestId" | "fullWidth"
  >;
  actionMenuItems?: MenuItem[];
  selectionLabel?: {
    single: string;
    multiple: string;
  };
};

export function BulkActions({
  notice,
  selectedCount,
  isLoading,
  isError = false,
  onDownload,
  onClear,
  primaryAction,
  secondaryAction,
  actionMenuItems,
  selectionLabel,
}: BulkActionsProps) {
  const isCompact = useDeviceWidth("lg");

  if (selectedCount === 0) return null;

  return (
    <Card
      className="fixed bottom-0 lg:bottom-20 left-0 lg:left-1/2 z-banner lg:h-17 w-screen lg:max-w-4xl lg:-translate-x-1/2 shadow-card rounded-3xl lg:rounded-companyBorderRadius"
      data-testid="bulk-actions"
    >
      {notice ? (
        <div className="absolute -top-16 left-0 w-full h-full">{notice}</div>
      ) : null}
      <div
        className="m-auto h-3 w-12 bg-background/tertiary rounded-full my-5 lg:hidden"
        data-testid="mobile-handle-bar"
      />
      <UnknownErrorModal open={isError} />
      <div className="flex flex-col lg:flex-row items-center justify-between px-3 pb-3 lg:p-4 h-full lg:gap-20 gap-4">
        <div className="text-text/primary body-base-medium lg:whitespace-nowrap lowercase flex gap-3 w-full justify-between lg:justify-start">
          <div>
            {selectedCount}{" "}
            {selectedCount === 1
              ? selectionLabel?.single || m.document_document()
              : selectionLabel?.multiple || m.document_documents()}{" "}
            {m.documents_selected()}
          </div>
          <ThemedButton
            className="capitalize"
            dataTestId="clear-button"
            onClick={onClear}
            variant="text"
          >
            {m.btn_clear()}
          </ThemedButton>
        </div>
        <div className="flex flex-col lg:flex-row gap-4 lg:max-w-[40rem] justify-end w-full">
          {onDownload ? (
            <ThemedButton
              className="w-full lg:w-auto"
              dataTestId="download-button"
              icon={<DownloadIcon />}
              iconPosition="left"
              isLoading={isLoading}
              onClick={onDownload}
            >
              {m.btn_downloadDocuments()}
            </ThemedButton>
          ) : null}
          {secondaryAction ? (
            <ThemedButton
              className="w-full lg:w-auto"
              dataTestId="bulk-secondary-action-button"
              variant="secondary"
              {...secondaryAction}
            >
              {secondaryAction.children}
            </ThemedButton>
          ) : null}
          {primaryAction ? (
            <ThemedButton
              className="w-full lg:w-auto"
              dataTestId="bulk-secondary-button"
              {...primaryAction}
            >
              {primaryAction.children}
            </ThemedButton>
          ) : null}

          {actionMenuItems ? (
            <ActionMenu
              className="flex items-center justify-center h-full"
              compact={isCompact}
              fullWidth={!isCompact}
              menuItems={actionMenuItems}
            />
          ) : null}
        </div>
      </div>
    </Card>
  );
}
